import { useMemo, useRef, useState } from "react";
import { addLocalDays, localDateString, type RestaurantDemoReservation, type RestaurantDemoState, type RestaurantDemoTable } from "./restaurant-sandbox";
import { StatusBadge } from "./restaurant-demo-v4-ui";

export type RestaurantTimelineScale = 15 | 30 | 60;

interface RestaurantTimelineProps {
  readonly state: RestaurantDemoState;
  readonly date: string;
  readonly scale: RestaurantTimelineScale;
  readonly area: string;
  readonly onDateChange: (date: string) => void;
  readonly onScaleChange: (scale: RestaurantTimelineScale) => void;
  readonly onReservationSelect: (reservation: RestaurantDemoReservation) => void;
  readonly onTableSelect?: (table: RestaurantDemoTable) => void;
  readonly onReservationMove: (reservation: RestaurantDemoReservation, tableId: string, startTime: string) => void;
  readonly onReservationResize: (reservation: RestaurantDemoReservation, durationMinutes: number) => void;
}

const START_MINUTES = 17 * 60;
const END_MINUTES = 25 * 60;
const LABEL_WIDTH = 174;

function minutesFromTime(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return (hours! * 60) + (minutes ?? 0);
}

function timeFromAbsoluteMinutes(value: number): string {
  const normalized = ((value % (24 * 60)) + (24 * 60)) % (24 * 60);
  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
}

function formatDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "2-digit", month: "short" }).format(new Date(year!, month! - 1, day!));
}

function reservationTone(reservation: RestaurantDemoReservation): "info" | "success" | "neutral" | "danger" {
  if (reservation.status === "seated") return "success";
  if (reservation.status === "completed") return "neutral";
  if (reservation.status === "cancelled" || reservation.status === "no_show") return "danger";
  return "info";
}

export function RestaurantTimeline(props: RestaurantTimelineProps) {
  const [isPanning, setIsPanning] = useState(false);
  const [resizePreview, setResizePreview] = useState<{ reservationId: string; durationMinutes: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const panRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
  const slotWidth = props.scale === 15 ? 54 : props.scale === 30 ? 74 : 104;
  const slots = useMemo(() => Array.from({ length: Math.ceil((END_MINUTES - START_MINUTES) / props.scale) }, (_, index) => START_MINUTES + index * props.scale), [props.scale]);
  const timelineWidth = slots.length * slotWidth;
  const tables = props.state.tables
    .filter((table) => table.active && (props.area === "all" || table.area === props.area))
    .sort((a, b) => a.area.localeCompare(b.area, "es") || a.label.localeCompare(b.label, "es", { numeric: true }));
  const reservations = props.state.reservations.filter((reservation) => reservation.date === props.date && !["cancelled", "no_show"].includes(reservation.status));

  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (target.closest("[data-reservation-block]") || target.closest("button,input,select")) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    panRef.current = { x: event.clientX, y: event.clientY, scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop };
    setIsPanning(true);
    viewport.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const viewport = viewportRef.current;
    const pan = panRef.current;
    if (!viewport || !pan) return;
    viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.x);
    viewport.scrollTop = pan.scrollTop - (event.clientY - pan.y);
  }

  function pointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    const viewport = viewportRef.current;
    if (viewport?.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
    panRef.current = null;
    setIsPanning(false);
  }

  function dropReservation(event: React.DragEvent<HTMLDivElement>, tableId: string) {
    event.preventDefault();
    const reservationId = event.dataTransfer.getData("text/restaurant-reservation");
    const reservation = props.state.reservations.find((item) => item.id === reservationId);
    if (!reservation) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(timelineWidth - 1, event.clientX - rect.left));
    const slot = Math.max(0, Math.min(slots.length - 1, Math.floor(relativeX / slotWidth)));
    props.onReservationMove(reservation, tableId, timeFromAbsoluteMinutes(slots[slot]!));
  }

  function beginResize(event: React.PointerEvent<HTMLButtonElement>, reservation: RestaurantDemoReservation) {
    event.stopPropagation();
    event.preventDefault();
    const startX = event.clientX;
    const initial = reservation.durationMinutes;
    const pxPerMinute = slotWidth / props.scale;
    function durationFor(clientX: number): number {
      const raw = initial + (clientX - startX) / pxPerMinute;
      return Math.max(props.scale, Math.round(raw / props.scale) * props.scale);
    }
    function onMove(moveEvent: PointerEvent) {
      moveEvent.preventDefault();
      setResizePreview({ reservationId: reservation.id, durationMinutes: durationFor(moveEvent.clientX) });
    }
    function onUp(upEvent: PointerEvent) {
      const snapped = durationFor(upEvent.clientX);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setResizePreview(null);
      props.onReservationResize(reservation, snapped);
    }
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { once: true });
  }

  return <section className="rdv5-timeline-shell">
    <div className="rdv5-timeline-controls">
      <div className="rdv5-date-nav">
        <button type="button" onClick={() => props.onDateChange(addLocalDays(props.date, -1))}>←</button>
        <button type="button" onClick={() => props.onDateChange(localDateString())}>Hoy</button>
        <strong>{formatDate(props.date)}</strong>
        <button type="button" onClick={() => props.onDateChange(addLocalDays(props.date, 1))}>→</button>
      </div>
      <div className="rdv5-scale-control" aria-label="Escala temporal">
        {([15, 30, 60] as const).map((scale) => <button type="button" key={scale} className={props.scale === scale ? "active" : ""} onClick={() => props.onScaleChange(scale)}>{scale} min</button>)}
      </div>
      <span className="rdv5-pan-hint">Arrastrá el fondo para navegar · Arrastrá una reserva para moverla</span>
    </div>

    <div
      ref={viewportRef}
      className={`rdv5-timeline-viewport${isPanning ? " is-panning" : ""}`}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerEnd}
      onPointerCancel={pointerEnd}
    >
      <div className="rdv5-timeline-canvas" style={{ width: LABEL_WIDTH + timelineWidth }}>
        <div className="rdv5-timeline-header" style={{ width: LABEL_WIDTH + timelineWidth }}>
          <div className="rdv5-timeline-corner" style={{ width: LABEL_WIDTH }}>Mesa / espacio</div>
          <div className="rdv5-timeline-hours" style={{ width: timelineWidth }}>
            {slots.map((minute) => <div key={minute} style={{ width: slotWidth }}><strong>{timeFromAbsoluteMinutes(minute)}</strong></div>)}
          </div>
        </div>

        {tables.map((table) => {
          const tableReservations = reservations.filter((reservation) => reservation.tableId === table.id);
          return <div className="rdv5-timeline-row" key={table.id} style={{ width: LABEL_WIDTH + timelineWidth }}>
            <button type="button" className="rdv5-timeline-table" style={{ width: LABEL_WIDTH }} onClick={() => props.onTableSelect?.(table)}>
              <strong>{table.label}</strong><span>{table.area} · {table.capacity}p</span>
            </button>
            <div
              className="rdv5-timeline-track"
              style={{ width: timelineWidth, backgroundSize: `${slotWidth}px 100%` }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => dropReservation(event, table.id)}
            >
              {tableReservations.map((reservation) => {
                let start = minutesFromTime(reservation.startTime);
                if (start < START_MINUTES) start += 24 * 60;
                const duration = resizePreview?.reservationId === reservation.id ? resizePreview.durationMinutes : reservation.durationMinutes;
                const left = Math.max(0, (start - START_MINUTES) / props.scale * slotWidth);
                const width = Math.max(slotWidth * .8, duration / props.scale * slotWidth);
                return <div
                  key={reservation.id}
                  data-reservation-block
                  draggable
                  onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/restaurant-reservation", reservation.id); }}
                  className={`rdv5-reservation-block status-${reservation.status}${resizePreview?.reservationId === reservation.id ? " is-resizing" : ""}`}
                  style={{ left, width }}
                  onClick={() => props.onReservationSelect(reservation)}
                >
                  <div><strong>{reservation.customerName}</strong><span>{reservation.partySize}p · {reservation.startTime}–{timeFromAbsoluteMinutes(start + duration)}</span></div>
                  <StatusBadge tone={reservationTone(reservation)}>{reservation.status === "seated" ? "Sentada" : reservation.status === "confirmed" ? "Confirmada" : reservation.status === "completed" ? "Completada" : reservation.status}</StatusBadge>
                  <button className="rdv5-resize-handle" aria-label={`Cambiar duración de ${reservation.customerName}`} type="button" onPointerDown={(event) => beginResize(event, reservation)} />
                </div>;
              })}
            </div>
          </div>;
        })}
      </div>
    </div>
  </section>;
}